import React, { useEffect, useState } from "react";
import { Briefcase, Eye, FileDown, FileText, Loader2 } from "lucide-react";
import api from "../../services/api";
import { openPdfInViewer } from "../PdfViewer";
import { btnGhost, btnPrimary, cls } from "../ui";
import { toast } from "sonner";

const CAT_ORDER = ["Positioning", "Commercial", "Sales Enablement", "Technical", "Delivery"];

export default function SalesKit() {
  const [docs, setDocs] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/ujms/sales-kit").then((r) => setDocs(r.data.documents)).catch(() => setDocs([]));
  }, []);

  const downloadBlob = async (url, filename, key) => {
    setBusy(key);
    try {
      const res = await api.get(url, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Download failed");
    } finally { setBusy(null); }
  };

  if (!docs) return <div className="flex items-center gap-2 text-slate-500 p-8"><Loader2 className="w-5 h-5 animate-spin" /> Loading sales kit…</div>;

  const byCat = CAT_ORDER.map((c) => [c, docs.filter((d) => d.category === c)]).filter(([, list]) => list.length);

  return (
    <div className="space-y-5" data-testid="sales-kit-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
          <Briefcase className="w-6 h-6" /> Sales Kit
        </h1>
        <p className="text-sm text-slate-500">
          Government sales &amp; procurement package — 9 branded documents. View or download each as PDF, or grab the
          editable Markdown source to customize before sending.
        </p>
      </div>
      {byCat.map(([cat, list]) => (
        <div key={cat}>
          <div className="tyler-panel-title text-sm mb-2" data-testid={`sk-cat-${cat.toLowerCase().replace(/\s+/g, "-")}`}>{cat.toLowerCase()}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {list.map((d) => (
              <div key={d.slug} className="border border-[#d1ddeb] bg-white rounded-sm p-4 flex flex-col" data-testid={`sk-card-${d.slug}`}>
                <div className="flex items-start gap-2.5">
                  <FileText className="w-5 h-5 text-ujms-dark shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 text-[14px] leading-snug">{d.title}</div>
                    <div className="text-[12px] text-slate-500 mt-1 leading-relaxed">{d.description}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 mt-4 pt-3 border-t border-slate-100">
                  <button className={btnPrimary} data-testid={`sk-view-${d.slug}`}
                    onClick={() => openPdfInViewer(`/ujms/sales-kit/${d.slug}/pdf`, d.title)}>
                    <Eye className="w-3.5 h-3.5" /> View PDF
                  </button>
                  <button className={cls(btnGhost)} disabled={busy === `${d.slug}-pdf`} data-testid={`sk-pdf-${d.slug}`}
                    onClick={() => downloadBlob(`/ujms/sales-kit/${d.slug}/pdf`, `CJMS_${d.slug.replace(/-/g, "_")}.pdf`, `${d.slug}-pdf`)}>
                    {busy === `${d.slug}-pdf` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} PDF
                  </button>
                  <button className={cls(btnGhost)} disabled={busy === `${d.slug}-md`} data-testid={`sk-md-${d.slug}`}
                    onClick={() => downloadBlob(`/ujms/sales-kit/${d.slug}/source`, `CJMS_${d.slug.replace(/-/g, "_")}.md`, `${d.slug}-md`)}>
                    {busy === `${d.slug}-md` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} Source (.md)
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <p className="text-[11px] text-slate-400">
        All documents carry placeholder company identifiers ([INSERT UEI], contact info, etc.) — edit the Markdown
        source, then regenerate. Vendor identity: "CJMS Justice Platform" (fictional demonstration brand).
      </p>
    </div>
  );
}
